import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel,
  SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowRight, ArrowLeftRight, Loader2, RefreshCw } from "lucide-react";
import { formatCurrency } from "@/lib/format";

const SYM = { USD: "$", NGN: "₦" };

// Encoded picker option value: "wallet:<id>" or "investment:<id>"
function encode(kind, id) { return `${kind}:${id}`; }
function decode(v) {
  if (!v) return null;
  const i = v.indexOf(":");
  return { kind: v.slice(0, i), id: v.slice(i + 1) };
}

export default function TransferDialog({ open, onClose, onDone, defaultFromId = null }) {
  const [accounts, setAccounts] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [fromVal, setFromVal] = useState("");
  const [toVal, setToVal] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [memo, setMemo] = useState("");
  const [fxRate, setFxRate] = useState(""); // manual override; empty = use live
  const [liveFxRate, setLiveFxRate] = useState(null);
  const [liveFxLoading, setLiveFxLoading] = useState(false);
  const [investmentMode, setInvestmentMode] = useState("cash"); // cash | buy
  const [investmentQty, setInvestmentQty] = useState("");
  const [saveTpl, setSaveTpl] = useState(false);
  const [tplName, setTplName] = useState("");
  const [saving, setSaving] = useState(false);
  // iter 76 — memoise destination lists so the dropdown doesn't rebuild on
  // every unrelated state change (fxRate, memo keystrokes, etc.).
  const toAccounts = useMemo(
    () => accounts.filter((a) => encode("wallet", a.id) !== fromVal),
    [accounts, fromVal],
  );
  const toInvestments = useMemo(
    () => investments.filter((i) => encode("investment", i.id) !== fromVal),
    [investments, fromVal],
  );

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [{ data: accs }, { data: invs }] = await Promise.all([
          api.get("/accounts"),
          api.get("/investments"),
        ]);
        setAccounts(accs || []);
        setInvestments(invs || []);
      } catch (err) {
        console.warn("[Transfer] load pickers failed", err?.message);
      }
    })();
    // Reset form state when reopening
    setAmount(""); setMemo(""); setFxRate(""); setLiveFxRate(null);
    setInvestmentMode("cash"); setInvestmentQty("");
    setDate(new Date().toISOString().slice(0, 10));
    if (defaultFromId) setFromVal(encode("wallet", defaultFromId));
    else setFromVal("");
    setToVal("");
  }, [open, defaultFromId]);

  const from = useMemo(() => decode(fromVal), [fromVal]);
  const to = useMemo(() => decode(toVal), [toVal]);

  // Resolve source + destination to concrete records + currency
  const fromRec = useMemo(() => {
    if (!from) return null;
    if (from.kind === "wallet") return accounts.find((a) => a.id === from.id);
    return investments.find((i) => i.id === from.id);
  }, [from, accounts, investments]);
  const toRec = useMemo(() => {
    if (!to) return null;
    if (to.kind === "wallet") return accounts.find((a) => a.id === to.id);
    return investments.find((i) => i.id === to.id);
  }, [to, accounts, investments]);

  const fromCcy = fromRec?.currency || "USD";
  const toCcy = toRec?.currency || fromCcy;
  const isCrossCcy = fromRec && toRec && fromCcy !== toCcy;
  const dstIsInvestment = to?.kind === "investment";

  // Fetch live FX rate whenever the source/destination currency pair changes
  useEffect(() => {
    if (!isCrossCcy || !fromRec || !toRec) { setLiveFxRate(null); return; }
    setLiveFxLoading(true);
    api.get("/fx/rates", { params: { base: fromCcy } })
      .then(({ data }) => {
        const r = data?.rates?.[toCcy];
        if (r && r > 0) setLiveFxRate(r);
      })
      .catch((err) => {
        console.warn("[Transfer] FX rate fetch failed", err?.message);
        setLiveFxRate(null);
      })
      .finally(() => setLiveFxLoading(false));
  }, [isCrossCcy, fromCcy, toCcy, fromRec, toRec]);

  const effectiveFxRate = fxRate ? parseFloat(fxRate) : liveFxRate;
  const toAmount = useMemo(() => {
    const a = parseFloat(amount);
    if (!a || a <= 0) return null;
    if (!isCrossCcy) return a;
    if (!effectiveFxRate || effectiveFxRate <= 0) return null;
    return Math.round(a * effectiveFxRate * 100) / 100;
  }, [amount, isCrossCcy, effectiveFxRate]);

  const canSubmit = fromRec && toRec && parseFloat(amount) > 0 && date &&
    !(isCrossCcy && !effectiveFxRate) &&
    !(dstIsInvestment && investmentMode === "buy" && (isCrossCcy || !investmentQty || parseFloat(investmentQty) <= 0));

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const body = {
        from: { kind: from.kind, id: from.id },
        to: { kind: to.kind, id: to.id },
        amount: parseFloat(amount),
        date,
        memo: memo || null,
      };
      if (isCrossCcy && fxRate) body.fx_rate = parseFloat(fxRate);
      if (dstIsInvestment) {
        body.investment_mode = investmentMode;
        if (investmentMode === "buy") body.investment_quantity = parseFloat(investmentQty);
      }
      const { data } = await api.post("/transfers", body);
      // Optionally save this transfer setup as a reusable template
      if (saveTpl && tplName.trim()) {
        try {
          await api.post("/transfer-templates", {
            name: tplName.trim(),
            from_kind: from.kind, from_id: from.id,
            to_kind: to.kind, to_id: to.id,
            amount: parseFloat(amount),
            memo: memo || null,
            investment_mode: dstIsInvestment ? investmentMode : null,
            investment_quantity: dstIsInvestment && investmentMode === "buy" ? parseFloat(investmentQty) : null,
          });
          toast.success(`Template "${tplName.trim()}" saved`);
        } catch (err) {
          toast.error("Transfer posted but template save failed");
        }
      }
      const msg = data.mode === "investment_buy"
        ? `Bought ${data.to.name || "investment"} · ${SYM[data.to.currency] || ""}${data.to.amount}`
        : `Transferred ${SYM[data.from.currency] || ""}${data.from.amount.toLocaleString()} → ${SYM[data.to.currency] || ""}${data.to.amount.toLocaleString()}`;
      toast.success(msg);
      onDone?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Transfer failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="transfer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowLeftRight className="w-5 h-5 text-[var(--brand-text)]" />
            Journal Transfer
          </DialogTitle>
          <DialogDescription>
            Move funds between wallets, credit cards, or investment positions.
            Posts a balanced journal entry automatically. Cross-currency
            transfers use the live Frankfurter FX rate.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* From */}
          <div>
            <Label className="text-xs tracking-wide uppercase text-[var(--muted-text)]">From</Label>
            <Select value={fromVal} onValueChange={setFromVal}>
              <SelectTrigger data-testid="transfer-from" className="mt-1 h-10">
                <SelectValue placeholder="Pick source" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Wallets & Cards</SelectLabel>
                  {accounts.map((a) => (
                    <SelectItem key={a.id} value={encode("wallet", a.id)} data-testid={`transfer-from-w-${a.id}`}>
                      {a.name} · {a.type} · {SYM[a.currency] || a.currency}
                    </SelectItem>
                  ))}
                </SelectGroup>
                {investments.length > 0 && (
                  <SelectGroup>
                    <SelectLabel>Investments</SelectLabel>
                    {investments.map((i) => (
                      <SelectItem key={i.id} value={encode("investment", i.id)} data-testid={`transfer-from-i-${i.id}`}>
                        {i.symbol || i.name} · {i.kind} · {SYM[i.currency] || i.currency}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* To */}
          <div>
            <Label className="text-xs tracking-wide uppercase text-[var(--muted-text)]">To</Label>
            <Select value={toVal} onValueChange={setToVal}>
              <SelectTrigger data-testid="transfer-to" className="mt-1 h-10">
                <SelectValue placeholder="Pick destination" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Wallets & Cards</SelectLabel>
                  {toAccounts.map((a) => (
                    <SelectItem key={a.id} value={encode("wallet", a.id)} data-testid={`transfer-to-w-${a.id}`}>
                      {a.name} · {a.type} · {SYM[a.currency] || a.currency}
                    </SelectItem>
                  ))}
                </SelectGroup>
                {investments.length > 0 && (
                  <SelectGroup>
                    <SelectLabel>Investments</SelectLabel>
                    {toInvestments.map((i) => (
                      <SelectItem key={i.id} value={encode("investment", i.id)} data-testid={`transfer-to-i-${i.id}`}>
                        {i.symbol || i.name} · {i.kind} · {SYM[i.currency] || i.currency}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Amount + Date row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs tracking-wide uppercase text-[var(--muted-text)]">
                Amount {fromRec && `(${SYM[fromCcy] || fromCcy})`}
              </Label>
              <Input
                data-testid="transfer-amount"
                type="number"
                min="0.01"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="mt-1 h-10 font-mono-data"
              />
            </div>
            <div>
              <Label className="text-xs tracking-wide uppercase text-[var(--muted-text)]">Date</Label>
              <Input
                data-testid="transfer-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="mt-1 h-10"
              />
            </div>
          </div>

          {/* FX rate — cross-currency only */}
          {isCrossCcy && (
            <div className="border border-[var(--brand)]/30 bg-[var(--brand-light)] rounded-md p-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs tracking-wide uppercase text-[var(--brand-text)]">
                  FX rate · {fromCcy} → {toCcy}
                </Label>
                {liveFxLoading && <Loader2 className="w-3 h-3 animate-spin text-[var(--brand-text)]" />}
              </div>
              <div className="flex items-center gap-2">
                <Input
                  data-testid="transfer-fx-rate"
                  type="number"
                  step="0.0001"
                  value={fxRate}
                  onChange={(e) => setFxRate(e.target.value)}
                  placeholder={liveFxRate ? `Live: ${liveFxRate.toFixed(4)}` : "Loading…"}
                  className="h-9 font-mono-data"
                />
                {liveFxRate && (
                  <Button
                    data-testid="transfer-fx-use-live"
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setFxRate("")}
                    className="h-9 shrink-0"
                    title="Use live rate"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" /> Live
                  </Button>
                )}
              </div>
              {toAmount != null && (
                <div className="mt-2 text-sm text-[var(--brand-text)]">
                  {SYM[fromCcy] || fromCcy}{parseFloat(amount || 0).toLocaleString()}
                  <ArrowRight className="w-3 h-3 mx-1 inline" />
                  <span className="font-medium">
                    {formatCurrency(toAmount, toCcy)}
                  </span>
                  <span className="text-xs opacity-70 ml-2">
                    at {(effectiveFxRate || 0).toFixed(4)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Investment-destination mode */}
          {dstIsInvestment && (
            <div className="border border-[var(--border)] rounded-md p-3 space-y-3">
              <Label className="text-xs tracking-wide uppercase text-[var(--muted-text)]">
                Investment action
              </Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  data-testid="transfer-inv-mode-cash"
                  onClick={() => setInvestmentMode("cash")}
                  className={`text-left rounded-md border p-2 text-sm transition ${
                    investmentMode === "cash"
                      ? "border-[var(--brand)] bg-[var(--brand-light)]"
                      : "border-[var(--border)] hover:bg-[var(--hover)]"
                  }`}
                >
                  <div className="font-medium">Move cash only</div>
                  <div className="text-xs text-[var(--muted-text)] mt-0.5">
                    Adds to brokerage cash. No new shares.
                  </div>
                </button>
                <button
                  type="button"
                  data-testid="transfer-inv-mode-buy"
                  onClick={() => setInvestmentMode("buy")}
                  disabled={isCrossCcy}
                  className={`text-left rounded-md border p-2 text-sm transition ${
                    investmentMode === "buy"
                      ? "border-[var(--brand)] bg-[var(--brand-light)]"
                      : "border-[var(--border)] hover:bg-[var(--hover)]"
                  } ${isCrossCcy ? "opacity-40 cursor-not-allowed" : ""}`}
                >
                  <div className="font-medium">Buy shares</div>
                  <div className="text-xs text-[var(--muted-text)] mt-0.5">
                    Updates position qty + cost basis.
                  </div>
                </button>
              </div>
              {investmentMode === "buy" && (
                <div>
                  <Label className="text-xs tracking-wide uppercase text-[var(--muted-text)]">
                    Quantity
                  </Label>
                  <Input
                    data-testid="transfer-inv-qty"
                    type="number"
                    min="0.0001"
                    step="0.0001"
                    value={investmentQty}
                    onChange={(e) => setInvestmentQty(e.target.value)}
                    placeholder="e.g. 5"
                    className="mt-1 h-9 font-mono-data"
                  />
                </div>
              )}
            </div>
          )}

          {/* Memo */}
          <div>
            <Label className="text-xs tracking-wide uppercase text-[var(--muted-text)]">
              Memo (optional)
            </Label>
            <Textarea
              data-testid="transfer-memo"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              placeholder="e.g. Fund emergency savings"
              rows={2}
              className="mt-1"
            />
          </div>

          {/* Save as template */}
          <div className="border-t border-[var(--divider)] pt-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                data-testid="transfer-save-template"
                checked={saveTpl}
                onChange={(e) => setSaveTpl(e.target.checked)}
                className="rounded"
              />
              <span>Save as a template for one-tap re-use</span>
            </label>
            {saveTpl && (
              <Input
                data-testid="transfer-template-name"
                value={tplName}
                onChange={(e) => setTplName(e.target.value)}
                placeholder="e.g. Monthly Amex payment"
                className="mt-2 h-9"
              />
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="transfer-cancel">
            Cancel
          </Button>
          <Button
            data-testid="transfer-submit"
            onClick={submit}
            disabled={!canSubmit || saving}
            className="btn-brand"
          >
            {saving ? "Posting…" : "Post transfer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
